import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CommercialStatusRow {
  device_id: string | null;
  organization_id: string | null;
  deal_id: string | null;
  plan: string | null;
  is_pilot: boolean | null;
  is_free_camera: boolean | null;
}

export interface CommercialFlags {
  isPilot: boolean;
  isFreeCamera: boolean;
}

const EMPTY: CommercialStatusRow[] = [];

/**
 * Read-only commercial flags (Pilot Pack / Free Camera) derived from won deals.
 * Degrades safely to "no flags" if the view is unavailable for this user.
 */
export const useCommercialStatus = () => {
  const query = useQuery({
    queryKey: ["commercial-status"],
    queryFn: async (): Promise<CommercialStatusRow[]> => {
      const { data, error } = await (supabase as any)
        .from("commercial_status")
        .select("device_id, organization_id, deal_id, plan, is_pilot, is_free_camera");

      if (error) {
        console.warn("commercial_status unavailable:", error.message);
        return EMPTY;
      }
      return (data as CommercialStatusRow[]) || EMPTY;
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  const rows = query.data || EMPTY;

  const deviceFlags = new Map<string, CommercialFlags>();
  const orgFlags = new Map<string, CommercialFlags>();
  const orgsWithDeviceRows = new Set<string>();

  for (const row of rows) {
    if (row.device_id) {
      if (row.organization_id) orgsWithDeviceRows.add(row.organization_id);
      const existing = deviceFlags.get(row.device_id) || { isPilot: false, isFreeCamera: false };
      deviceFlags.set(row.device_id, {
        isPilot: existing.isPilot || !!row.is_pilot,
        isFreeCamera: existing.isFreeCamera || !!row.is_free_camera,
      });
    }
    if (row.organization_id) {
      const existing = orgFlags.get(row.organization_id) || { isPilot: false, isFreeCamera: false };
      orgFlags.set(row.organization_id, {
        isPilot: existing.isPilot || !!row.is_pilot,
        isFreeCamera: existing.isFreeCamera || !!row.is_free_camera,
      });
    }
  }

  const noFlags: CommercialFlags = { isPilot: false, isFreeCamera: false };

  return {
    ...query,
    rows,
    getDeviceFlags: (deviceId?: string | null): CommercialFlags =>
      (deviceId && deviceFlags.get(deviceId)) || noFlags,
    getOrgFlags: (organizationId?: string | null): CommercialFlags =>
      (organizationId && orgFlags.get(organizationId)) || noFlags,
    /**
     * Device-level flags, falling back to org-level only when the organization
     * has no device-level rows at all (e.g. deal won but devices not yet allocated).
     */
    getFlagsForDevice: (deviceId?: string | null, organizationId?: string | null): CommercialFlags => {
      const direct = (deviceId && deviceFlags.get(deviceId)) || null;
      if (direct) return direct;
      if (organizationId && !orgsWithDeviceRows.has(organizationId)) {
        return orgFlags.get(organizationId) || noFlags;
      }
      return noFlags;
    },
  };
};
